/**
 * Template Editor View Component
 * Full-screen template editor with GrapesJS integration
 * Handles both creating new templates and editing existing ones
 * with proper system vs user template logic
 *
 * @file src/components/email/templates/TemplateEditorView.tsx
 * @created 2025-10-27
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Save, Eye, AlertCircle, ChevronDown, Loader2, Mail } from 'lucide-react';
import { EmailTemplate } from '@/api/email';
import GrapesJSEditor from '../editor/GrapesJSEditor';
import PreviewDialog from '../preview/PreviewDialog';
import { toast } from '@/hooks/use-toast';
import EmailService from '@/api/email';
import mjml2html from 'mjml-browser';
import { useAuth } from '@/context/auth';
import { supabase } from '@/integrations/supabase/client';

interface TemplateEditorViewProps {
  template?: EmailTemplate | null;
  customizedDesignJson?: Record<string, any> | null;
  organisationId: string;
  restaurantId?: string;
  onSave: () => void;
  onCancel: () => void;
}

const TEMPLATE_CATEGORIES = [
  { value: 'promotional', label: 'Promotional' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'trigger', label: 'Automated/Trigger' },
  { value: 'other', label: 'Other' },
];

/**
 * TemplateEditorView Component
 *
 * Features:
 * - Full-screen GrapesJS editor
 * - Template metadata form (collapsible)
 * - System vs User template save logic
 * - Preview functionality
 * - Unsaved changes warning
 *
 * @example
 * <TemplateEditorView
 *   template={template}
 *   customizedDesignJson={designJson}
 *   organisationId="org-123"
 *   onSave={() => handleSave()}
 *   onCancel={() => handleCancel()}
 * />
 */
const TemplateEditorView: React.FC<TemplateEditorViewProps> = ({
  template,
  customizedDesignJson,
  organisationId,
  restaurantId,
  onSave,
  onCancel
}) => {
  const { user } = useAuth();
  const isSystemTemplate = template?.is_system_template || false;
  const isNewTemplate = !template;
  const isEditingSystemTemplate = template?.is_system_template && customizedDesignJson;

  // Form state
  const [templateData, setTemplateData] = useState({
    name: template?.template_name || '',
    subject: template?.template_subject || '',
    category: template?.category || 'other',
    description: template?.description || '',
  });

  const [designJson, setDesignJson] = useState<Record<string, any> | null>(
    customizedDesignJson || template?.design_json || null
  );
  const [contentHtml, setContentHtml] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMetadata, setShowMetadata] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Editor refs
  const editorSaveRef = useRef<(() => Promise<void>) | null>(null);
  const editorInstanceRef = useRef<any | null>(null);

  // Preview dialog state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [resolvedPreview, setResolvedPreview] = useState<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: { id: string; email: string; first_name: string; last_name: string };
  } | null>(null);

  // Customer preview state (for real variable resolution)
  const [recentCustomers, setRecentCustomers] = useState<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    total_orders?: number;
  }>>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);

  // Fetch recent customers for preview
  useEffect(() => {
    const fetchRecentCustomers = async () => {
      if (!user?.organisationId) return;

      try {
        console.log('[TemplateEditorView] Fetching recent customers for preview');
        const customers = await EmailService.getRecentCustomers(user.organisationId, 50);
        setRecentCustomers(customers);

        // Auto-select first customer
        if (customers.length > 0) {
          setSelectedCustomerId(customers[0].id);
        }
      } catch (error) {
        console.error('[TemplateEditorView] Error fetching customers:', error);
      }
    };

    fetchRecentCustomers();
  }, [user?.organisationId]);

  // Validation
  const validateForm = (): boolean => {
    if (!templateData.name.trim()) {
      setValidationError('Template name is required');
      return false;
    }

    if (templateData.name.length > 100) {
      setValidationError('Template name must be 100 characters or less');
      return false;
    }

    if (!templateData.subject.trim()) {
      setValidationError('Subject line is required');
      return false;
    }

    setValidationError(null);
    return true;
  };

  // Handle save (update existing template)
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    if (isSystemTemplate && !customizedDesignJson) {
      toast({
        title: 'Cannot save system template',
        description: 'System templates are read-only. Use "Save as New" instead.',
        variant: 'destructive',
      });
      return;
    }

    if (!template?.id) {
      toast({
        title: 'Cannot update template',
        description: 'No template selected. Use "Save as New" to create a template.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      console.log('[TemplateEditorView] Saving template:', templateData.name);

      // FIX: Manually get current editor state instead of relying on stale state
      // Since projectId=undefined, onChange never fires, so we must get state directly
      let currentProjectData = designJson;
      let currentHtml = contentHtml;

      if (editorInstanceRef.current) {
        console.log('[TemplateEditorView] Getting current editor state directly');
        currentProjectData = editorInstanceRef.current.getProjectData();

        // Get MJML and compile to HTML (same as Storage Manager logic)
        const mjmlMarkup = editorInstanceRef.current.getHtml();

        try {
          const mjmlResult = mjml2html(mjmlMarkup);
          currentHtml = mjmlResult.html; // ✅ Compiled HTML

          if (mjmlResult.errors && mjmlResult.errors.length > 0) {
            console.warn('[TemplateEditorView] MJML compilation warnings:', mjmlResult.errors);
          }

          console.log('[TemplateEditorView] MJML compiled successfully, HTML length:', currentHtml.length);
        } catch (error) {
          console.error('[TemplateEditorView] MJML compilation failed:', error);
          currentHtml = mjmlMarkup; // Fallback to MJML if compilation fails
        }
      } else {
        console.warn('[TemplateEditorView] Editor instance not available, using state values');
      }

      // Generate plain text version - comprehensive HTML stripping
      const plainText = currentHtml
        // Remove head section (contains styles, meta, etc.)
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
        // Remove style blocks
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        // Remove script blocks
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        // Remove XML/Office tags (<!--[if mso]> ... <![endif]-->)
        .replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '')
        // Remove HTML comments
        .replace(/<!--[\s\S]*?-->/g, '')
        // Remove all HTML tags
        .replace(/<[^>]+>/g, '')
        // Remove HTML entities
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        // Collapse multiple spaces/newlines to single space
        .replace(/\s+/g, ' ')
        // Trim whitespace
        .trim();

      console.log('[TemplateEditorView] Generated plain text, length:', plainText.length);

      // Update existing template with CURRENT editor state
      await EmailService.updateTemplate(template.id, {
        template_name: templateData.name.trim(),
        template_subject: templateData.subject.trim(),
        template_content_html: currentHtml,
        template_content_text: plainText,
        design_json: currentProjectData,
        template_description: templateData.description.trim() || undefined,
        category: templateData.category,
      });

      toast({
        title: 'Template updated',
        description: `"${templateData.name}" has been updated successfully.`,
      });

      setHasUnsavedChanges(false);
      onSave();
    } catch (err: any) {
      console.error('[TemplateEditorView] Error saving template:', err);
      toast({
        title: 'Error saving template',
        description: err.message || 'Failed to save template. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle save as new
  const handleSaveAsNew = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      console.log('[TemplateEditorView] Saving as new template:', templateData.name);

      // FIX: Manually get current editor state instead of relying on stale state
      // Since projectId=undefined, onChange never fires, so we must get state directly
      let currentProjectData = designJson;
      let currentHtml = contentHtml;

      if (editorInstanceRef.current) {
        console.log('[TemplateEditorView] Getting current editor state directly');
        currentProjectData = editorInstanceRef.current.getProjectData();

        // Get MJML and compile to HTML (same as Storage Manager logic)
        const mjmlMarkup = editorInstanceRef.current.getHtml();

        try {
          const mjmlResult = mjml2html(mjmlMarkup);
          currentHtml = mjmlResult.html; // ✅ Compiled HTML

          if (mjmlResult.errors && mjmlResult.errors.length > 0) {
            console.warn('[TemplateEditorView] MJML compilation warnings:', mjmlResult.errors);
          }

          console.log('[TemplateEditorView] MJML compiled successfully, HTML length:', currentHtml.length);
        } catch (error) {
          console.error('[TemplateEditorView] MJML compilation failed:', error);
          currentHtml = mjmlMarkup; // Fallback to MJML if compilation fails
        }
      } else {
        console.warn('[TemplateEditorView] Editor instance not available, using state values');
      }

      // Generate plain text version - comprehensive HTML stripping
      const plainText = currentHtml
        // Remove head section (contains styles, meta, etc.)
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
        // Remove style blocks
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        // Remove script blocks
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        // Remove XML/Office tags (<!--[if mso]> ... <![endif]-->)
        .replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '')
        // Remove HTML comments
        .replace(/<!--[\s\S]*?-->/g, '')
        // Remove all HTML tags
        .replace(/<[^>]+>/g, '')
        // Remove HTML entities
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        // Collapse multiple spaces/newlines to single space
        .replace(/\s+/g, ' ')
        // Trim whitespace
        .trim();

      console.log('[TemplateEditorView] Generated plain text for new template, length:', plainText.length);

      // Create new template with CURRENT editor state
      await EmailService.createTemplate({
        template_name: templateData.name.trim(),
        template_subject: templateData.subject.trim(),
        template_content_html: currentHtml,
        template_content_text: plainText,
        design_json: currentProjectData,
        template_description: templateData.description.trim() || undefined,
        category: templateData.category,
        organisation_id: organisationId,
        restaurant_id: restaurantId,
        is_system_template: false,
        is_active: true,
      });

      toast({
        title: 'Template created',
        description: `"${templateData.name}" has been saved to your templates.`,
      });

      setHasUnsavedChanges(false);
      onSave();
    } catch (err: any) {
      console.error('[TemplateEditorView] Error creating template:', err);
      toast({
        title: 'Error creating template',
        description: err.message || 'Failed to create template. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) {
        return;
      }
    }

    onCancel();
  };

  // Handle preview - Show customer selector for ALL templates (saved and unsaved)
  const handlePreview = () => {
    console.log('[TemplateEditorView] Preview requested');

    // Check if we have customers
    if (recentCustomers.length === 0) {
      toast({
        title: 'No Customers Available',
        description: 'Add customers before previewing templates.',
        variant: 'destructive'
      });
      return;
    }

    // ✅ Show customer selector for BOTH saved and unsaved templates
    setShowCustomerSelector(true);
  };

  // Generate preview with selected customer (for both saved and unsaved templates)
  const handleGeneratePreview = async () => {
    if (!selectedCustomerId || !user?.organisationId) {
      toast({ title: 'Error', description: 'Please select a customer', variant: 'destructive' });
      return;
    }

    setPreviewLoading(true);
    try {
      let preview;

      // System templates and unsaved templates: compile MJML from editor
      // User templates that are saved: use backend endpoint
      const needsMjmlCompilation = !template?.id || (template?.is_system_template && customizedDesignJson);

      if (needsMjmlCompilation) {
        // ✅ Unsaved template OR system template being customized - compile MJML and use new endpoint
        console.log('[TemplateEditorView] Generating preview from editor MJML (unsaved or system template)');

        if (!editorInstanceRef.current) {
          toast({ title: 'Editor not ready', variant: 'destructive' });
          return;
        }

        // Compile MJML to HTML
        const mjml = editorInstanceRef.current.getHtml();
        const result = mjml2html(mjml);

        // Check for MJML compilation errors
        if (result.errors && result.errors.length > 0) {
          const hasErrors = result.errors.some((err: any) => err.type === 'error');
          if (hasErrors) {
            toast({
              title: 'MJML Compilation Warning',
              description: 'Some elements may not render correctly',
              variant: 'default'
            });
          }
        }

        // Call backend with compiled HTML and real customer data
        preview = await EmailService.previewTemplateContentWithCustomer(
          result.html,
          '',
          templateData.subject || template?.template_subject || 'Preview Subject',
          selectedCustomerId,
          user.organisationId
        );
      } else {
        // ✅ Saved user template - use existing endpoint
        console.log('[TemplateEditorView] Generating preview for saved user template');
        preview = await EmailService.previewTemplateWithCustomer(
          template.id,
          selectedCustomerId,
          user.organisationId
        );
      }

      setShowCustomerSelector(false);
      setResolvedPreview(preview);
      setShowPreviewDialog(true);

      toast({ title: 'Success', description: 'Preview generated with actual customer data' });
    } catch (error: any) {
      toast({ title: 'Preview Error', description: error.message, variant: 'destructive' });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-white">
      {/* Top Toolbar */}
      <div className="border-b px-6 py-4 flex items-center justify-between bg-white z-10">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex-1 max-w-md">
            <Input
              placeholder="Template Name"
              value={templateData.name}
              onChange={(e) => {
                setTemplateData({ ...templateData, name: e.target.value });
                setHasUnsavedChanges(true);
              }}
              className="font-medium"
              disabled={saving}
            />
          </div>

          <Badge variant="outline" className="shrink-0">
            {TEMPLATE_CATEGORIES.find(c => c.value === templateData.category)?.label || templateData.category}
          </Badge>

          {isEditingSystemTemplate && (
            <Badge variant="outline" className="bg-purple-100 text-purple-700 shrink-0">
              Customized System Template
            </Badge>
          )}

          {isSystemTemplate && !customizedDesignJson && (
            <Badge variant="outline" className="bg-purple-100 text-purple-700 shrink-0">
              System Template (Read-Only)
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePreview} disabled={saving}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>

          {/* Save buttons based on template type */}
          {isEditingSystemTemplate || (isSystemTemplate && customizedDesignJson) ? (
            // Customized system template: Can only save as new
            <Button size="sm" onClick={handleSaveAsNew} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save as New Template
                </>
              )}
            </Button>
          ) : isSystemTemplate ? (
            // System template without customization: Read-only
            <Button size="sm" disabled>
              <AlertCircle className="h-4 w-4 mr-2" />
              Read-Only
            </Button>
          ) : (
            // User template or new template: Can update or save as new
            <>
              {!isNewTemplate && (
                <Button variant="outline" size="sm" onClick={handleSaveAsNew} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  Save as New
                </Button>
              )}
              <Button
                size="sm"
                onClick={isNewTemplate ? handleSaveAsNew : handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isNewTemplate ? 'Save Template' : 'Update Template'}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200">
          <Alert variant="destructive" className="border-0 bg-transparent p-0">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{validationError}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Metadata Panel (Collapsible) - Above Editor */}
      <Collapsible open={showMetadata} onOpenChange={setShowMetadata}>
        <div className="border-b bg-gray-50">
          <CollapsibleTrigger className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors">
            <span className="text-sm font-medium">Template Settings</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showMetadata ? 'transform rotate-180' : ''}`} />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="max-h-[240px] overflow-y-auto border-b">
          <Card className="m-6 mt-0 mb-0 border-x-0 border-t-0 border-b-0 rounded-none shadow-none">
            <CardContent className="pt-4 pb-4 grid grid-cols-2 gap-4">
              {/* Subject Line */}
              <div className="space-y-2">
                <Label htmlFor="subject" className="required">
                  Default Subject Line
                </Label>
                <Input
                  id="subject"
                  placeholder="e.g., Check out our latest offers!"
                  value={templateData.subject}
                  onChange={(e) => {
                    setTemplateData({ ...templateData, subject: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                  disabled={saving}
                />
                <p className="text-xs text-gray-500">
                  You can change this when creating a campaign
                </p>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={templateData.category}
                  onValueChange={(value) => {
                    setTemplateData({ ...templateData, category: value });
                    setHasUnsavedChanges(true);
                  }}
                  disabled={saving}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2 col-span-2">
                <Label htmlFor="description">
                  Description <span className="text-gray-500 font-normal">(Optional)</span>
                </Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of when to use this template..."
                  value={templateData.description}
                  onChange={(e) => {
                    setTemplateData({ ...templateData, description: e.target.value });
                    setHasUnsavedChanges(true);
                  }}
                  rows={2}
                  maxLength={500}
                  disabled={saving}
                />
                <p className="text-xs text-gray-500">
                  {templateData.description.length}/500 characters
                </p>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {/* GrapesJS Editor */}
      <div className="flex-1 overflow-hidden">
        <GrapesJSEditor
          initialProject={designJson || undefined}
          projectId={undefined}
          projectType="template"
          onChange={(html, css, project) => {
            setContentHtml(html);
            setDesignJson(project);
            setHasUnsavedChanges(true);
          }}
          onEditorReady={async (saveFunc, editor) => {
            editorSaveRef.current = saveFunc;
            editorInstanceRef.current = editor;

            // NOTE: Asset management is now handled by GrapesJSEditor's loadInitialAssets
            // and the backend strips assets array from customized templates (see server/services/email/template-variables.js)
            // No additional asset reload needed here
            console.log('[TemplateEditorView] Editor ready');
          }}
          height="100%"
        />
      </div>

      {/* Customer Selector Dialog */}
      <Dialog open={showCustomerSelector} onOpenChange={setShowCustomerSelector}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Preview with Customer Data</DialogTitle>
            <DialogDescription>
              Select a customer to see real variable values
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Customer</Label>
              <Select value={selectedCustomerId || ''} onValueChange={setSelectedCustomerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a customer" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {recentCustomers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.first_name} {customer.last_name} ({customer.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerSelector(false)} disabled={previewLoading}>
              Cancel
            </Button>
            <Button onClick={handleGeneratePreview} disabled={!selectedCustomerId || previewLoading}>
              {previewLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Eye className="h-4 w-4 mr-2" />Preview</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {resolvedPreview && (
        <PreviewDialog
          open={showPreviewDialog}
          onOpenChange={setShowPreviewDialog}
          resolvedPreview={resolvedPreview}
          template={template ? {
            id: template.id,
            template_name: template.template_name,
            is_system_template: template.is_system_template
          } : undefined}
          userRole={user?.role}
        />
      )}
    </div>
  );
};

export default TemplateEditorView;
